<template>
  <ToolShell
    tool-name="Email signature generator"
    h1="Free email signature generator for freelancers and small teams."
    lede="Type your details, pick a layout and a color, and copy a clean signature into Gmail, Outlook or Apple Mail. Free, no signup, and nothing you type leaves your browser."
    :trust="['No signup', 'Gmail, Outlook & Apple Mail', 'Copy as HTML']"
    form-title="Signature details"
    :upsell-points="[
      'Each team member keeps a personal signature',
      'Shared mailboxes carry their own signature',
      'Every email you send lands on the client record',
    ]"
    free-label="The free generator"
    :free-bullets="[
      'One signature, pasted into one mail app',
      'You update it by hand in every app you use',
      'Client conversations stay scattered across inboxes',
    ]"
    upgrade-headline="This signature works in one inbox. Workspace369 connects the whole conversation."
    :connected-bullets="[
      'A personal signature per team member, plus one per shared mailbox',
      'Your signature goes into replies and new emails automatically',
      'Email, SMS and calls sit together on each client record',
      'Proposals, invoices and bookings go out from the same place',
    ]"
    slug="/tools/email-signature-generator"
    :meta-description="metaDescription"
    :doc-brand="details.name"
    :doc-subtitle="details.email"
    doc-kind="SIGNATURE"
    stamp="Preview"
  >
    <template #form>
      <div class="ta-field-row">
        <label class="ta-field">
          <span>Full name</span>
          <div class="ta-input">
            <input v-model.trim="form.name" type="text" :placeholder="sample.name" autocomplete="name" maxlength="80" />
          </div>
        </label>
        <label class="ta-field">
          <span>Job title</span>
          <div class="ta-input">
            <input v-model.trim="form.title" type="text" :placeholder="sample.title" autocomplete="organization-title" maxlength="80" />
          </div>
        </label>
      </div>

      <label class="ta-field">
        <span>Business name</span>
        <div class="ta-input">
          <input v-model.trim="form.company" type="text" :placeholder="sample.company" autocomplete="organization" maxlength="80" />
        </div>
      </label>

      <div class="ta-field-row">
        <label class="ta-field">
          <span>Email</span>
          <div class="ta-input">
            <input v-model.trim="form.email" type="email" :placeholder="sample.email" autocomplete="email" maxlength="120" />
          </div>
        </label>
        <label class="ta-field">
          <span>Phone (optional)</span>
          <div class="ta-input">
            <input v-model.trim="form.phone" type="tel" :placeholder="sample.phone" autocomplete="tel" maxlength="40" />
          </div>
        </label>
      </div>

      <label class="ta-field">
        <span>Website (optional)</span>
        <div class="ta-input">
          <input v-model.trim="form.website" type="url" :placeholder="sample.website" autocomplete="url" maxlength="200" />
        </div>
      </label>

      <div class="ta-field-row">
        <label class="ta-field">
          <span>Booking link (optional)</span>
          <div class="ta-input">
            <input v-model.trim="form.booking" type="url" placeholder="https://…" maxlength="300" />
          </div>
        </label>
        <label class="ta-field">
          <span>Booking button text</span>
          <div class="ta-input">
            <input v-model.trim="form.bookingLabel" type="text" placeholder="Book a call" maxlength="40" />
          </div>
        </label>
      </div>

      <div class="ta-field-row">
        <label class="ta-field">
          <span>LinkedIn URL (optional)</span>
          <div class="ta-input">
            <input v-model.trim="form.linkedin" type="url" placeholder="https://www.linkedin.com/in/…" maxlength="200" />
          </div>
        </label>
        <label class="ta-field">
          <span>Instagram URL (optional)</span>
          <div class="ta-input">
            <input v-model.trim="form.instagram" type="url" placeholder="https://www.instagram.com/…" maxlength="200" />
          </div>
        </label>
      </div>

      <label class="ta-field">
        <span>Photo or logo image URL (optional)</span>
        <div class="ta-input">
          <input v-model.trim="form.image" type="url" placeholder="https://yoursite.com/headshot.jpg" maxlength="400" />
        </div>
      </label>

      <div class="ta-field">
        <span id="signature-layout-label">Layout</span>
        <ToolDropdown
          v-model="form.layout"
          :options="layoutOptions"
          aria-label="Layout"
          labelledby="signature-layout-label"
        />
      </div>

      <div class="ta-field">
        <span id="signature-color-label">Accent color</span>
        <div class="sig-swatches" role="radiogroup" aria-labelledby="signature-color-label">
          <button
            v-for="color in colors"
            :key="color.value"
            type="button"
            role="radio"
            class="sig-swatch"
            :class="{ 'is-active': form.color === color.value }"
            :style="{ backgroundColor: color.value }"
            :aria-checked="form.color === color.value"
            :aria-label="color.label"
            @click="form.color = color.value"
          />
        </div>
      </div>

      <button type="button" class="ta-download" @click="copySignature">
        {{ copied === "rich" ? "Copied!" : "Copy signature" }}
      </button>
      <button type="button" class="ta-download" @click="copyHtml">
        {{ copied === "html" ? "Copied!" : "Copy HTML source" }}
      </button>

      <p class="ta-note">
        Paste with “Copy signature” into Gmail or Apple Mail settings. Use “Copy HTML source” for
        Outlook and anything that takes raw HTML. Images must be at a public https:// address.
      </p>
    </template>

    <template #preview>
      <div class="sig-preview" translate="no" v-html="signatureHtml" />
      <p class="ta-doc-note">
        The preview uses sample details until you type your own. Keep it to about four lines:
        mail apps and phones cut long signatures.
      </p>
    </template>

    <template #steps>
      <div><span>01</span><div><h2>Enter your details</h2><p>Name, title, business, and the contact links clients actually use. Leave out anything you don’t want shown.</p></div></div>
      <div><span>02</span><div><h2>Pick a layout and color</h2><p>Stacked, side by side with a photo, or a compact single block, in your brand color.</p></div></div>
      <div><span>03</span><div><h2>Copy it into your mail app</h2><p>One click copies the formatted signature; the HTML source is there for Outlook and other apps.</p></div></div>
    </template>

    <section class="tool-faq">
      <div class="tool-faq-inner">
        <h2>Email signature FAQ</h2>
        <div class="tool-faq-list">
          <details v-for="item in faq" :key="item.q" class="tool-faq-item">
            <summary>{{ item.q }}</summary>
            <p>{{ item.a }}</p>
          </details>
        </div>
      </div>
    </section>

    <RelatedPages path="/tools/email-signature-generator" title="Email signature generator" />

    <Cta03 />
  </ToolShell>
</template>

<script setup>
import ToolDropdown from "~/components/tools/ToolDropdown.vue";
import ToolShell from "~/components/tools/ToolShell.vue";
import { buildSignatureHtml, signatureText } from "~/utils/email-signature.mjs";

const sample = {
  name: "Alex Rivera",
  title: "Brand designer",
  company: "Rivera Studio",
  email: "alex@riverastudio.com",
  phone: "+1 (555) 014-2387",
  website: "https://riverastudio.com",
};

const form = reactive({
  name: "",
  title: "",
  company: "",
  email: "",
  phone: "",
  website: "",
  booking: "",
  bookingLabel: "",
  linkedin: "",
  instagram: "",
  image: "",
  layout: "stacked",
  color: "#0e7490",
});

const layoutOptions = [
  { value: "stacked", label: "Stacked", hint: "accent bar on the left" },
  { value: "photo", label: "Side by side", hint: "photo or logo first" },
  { value: "compact", label: "Compact", hint: "fewest lines" },
];

const colors = [
  { value: "#0e7490", label: "Teal" },
  { value: "#1d4ed8", label: "Blue" },
  { value: "#7c3aed", label: "Violet" },
  { value: "#be123c", label: "Rose" },
  { value: "#b45309", label: "Amber" },
  { value: "#15803d", label: "Green" },
  { value: "#111827", label: "Charcoal" },
];

const isEmpty = computed(() =>
  [
    "name", "title", "company", "email", "phone", "website",
    "booking", "bookingLabel", "linkedin", "instagram", "image",
  ].every((field) => !form[field].trim()),
);

const details = computed(() => {
  if (!isEmpty.value) return { ...form };
  return { ...form, ...sample };
});

const signatureHtml = computed(() => buildSignatureHtml(details.value));

const copied = ref("");
const flash = (kind) => {
  copied.value = kind;
  setTimeout(() => (copied.value = ""), 1600);
};

const copySignature = async () => {
  const html = signatureHtml.value;
  const text = signatureText(details.value);
  try {
    if (window.ClipboardItem && navigator.clipboard?.write) {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([text], { type: "text/plain" }),
        }),
      ]);
    } else {
      await navigator.clipboard.writeText(text);
    }
    flash("rich");
  } catch { /* clipboard unavailable */ }
};

const copyHtml = async () => {
  try {
    await navigator.clipboard.writeText(signatureHtml.value);
    flash("html");
  } catch { /* clipboard unavailable */ }
};

const faq = [
  {
    q: "How do I add the signature in Gmail?",
    a: "Click Copy signature. In Gmail open Settings, See all settings, General, then Signature. Create a new signature, paste it into the box, choose it as the default for new emails and replies, and save changes at the bottom of the page.",
  },
  {
    q: "How do I add it in Outlook?",
    a: "In Outlook on the web or the new Outlook, open Settings, Account, Signatures, create a signature and paste. If the formatting drops, paste the HTML source into a signature editor that accepts HTML, or paste the formatted copy from this page into a new email first and copy it from there.",
  },
  {
    q: "How do I add it in Apple Mail?",
    a: "Open Mail, Settings, Signatures, select the account, add a signature and paste. Untick “Always match my default message font” so your formatting and color are kept.",
  },
  {
    q: "Why doesn’t my photo or logo show?",
    a: "Mail apps load signature images from the internet, so the image needs a public https:// address, such as your website or a public image host. Files on your computer or behind a login won’t load for the people you email. Square images around 160 × 160 pixels look sharp.",
  },
  {
    q: "What should a freelancer’s email signature include?",
    a: "Your name, what you do, one way to reach you and one link that moves work forward, such as your booking page or portfolio. Skip quotes, long legal text and more than two social links; they push the useful lines out of view on phones.",
  },
  {
    q: "Is my information stored?",
    a: "No. The signature is built in your browser as you type. Nothing you enter is sent to Workspace369 or saved after you close the page.",
  },
];

const metaTitle = "Free Email Signature Generator | Workspace369";
const metaDescription =
  "Free email signature generator: build a clean signature with your photo, booking link and brand color, then copy it into Gmail, Outlook or Apple Mail.";

useHead(() => ({
  title: metaTitle,
  meta: [
    { name: "description", content: metaDescription },
    { name: "twitter:title", content: metaTitle },
    { name: "twitter:description", content: metaDescription },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:title", content: metaTitle },
    { property: "og:description", content: metaDescription },
  ],
  script: [
    {
      key: "tool-faq-schema",
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        })),
      }),
    },
  ],
}));
</script>

<style scoped>
.sig-swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.sig-swatch {
  width: 1.9rem;
  height: 1.9rem;
  border: 2px solid #fff;
  border-radius: 999px;
  box-shadow: 0 0 0 1px #d1d5db;
  cursor: pointer;
}
.sig-swatch.is-active {
  box-shadow: 0 0 0 2px #111827;
}
.sig-swatch:focus-visible {
  outline: 2px solid #111827;
  outline-offset: 2px;
}
.sig-preview {
  overflow-x: auto;
  padding: 1.25rem;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  color: #111827;
}

/* Dark mode: the site's dark tokens (page #020617, card #0f172a, hairline #243247, text #f1f5f9 / #cbd5e1 / #94a3b8, accent #67e8f9). Light styles above are unchanged. */
.dark .sig-swatch {
  border: 2px solid #243247;
}

.dark .sig-preview {
  border: 1px solid #243247;
  background: #0f172a;
  color: #f1f5f9;
}


</style>
